// Papéis e permissões da oficina — fonte única da API, do painel e do app.
// Plano: docs/ref/mustauto_permissoes_plano.md.
//
// A permissão é da PARTICIPAÇÃO na oficina (`workshop_members`), nunca do
// usuário. Cada participação guarda o papel e só o que difere dele:
//
//   permissões efetivas = padrão do papel ⊕ ajustes da pessoa
//
// Regra D10: ninguém ganha nem perde nada até o gestor mexer naquela pessoa.
// Por isso o padrão de cada papel que JÁ existe é o que esse papel faz hoje —
// o Mecânico é a coluna "Hoje" do catálogo do plano. Restrição só existe como
// ajuste que o gestor gravou.

/** Os grupos, na ordem em que a tela de permissões os mostra. */
export const PERMISSION_GROUPS = [
  {
    key: 'os',
    label: 'OS',
    permissions: [
      ['os.abrir', 'Abrir OS (com o checklist de entrada)'],
      ['os.executar', 'Registrar a execução: fotos, etapas, laudo, insumos'],
      ['os.itens', 'Incluir e tirar peças e serviços'],
      ['os.preco_manual', 'Digitar preço fora da tabela e do estoque'],
      ['os.orcamento', 'Enviar orçamento e registrar a resposta do cliente'],
      ['os.atribuir', 'Trocar o mecânico responsável'],
      ['os.cancelar', 'Cancelar OS'],
    ],
  },
  {
    key: 'fechamento',
    label: 'Fechamento',
    permissions: [
      ['os.concluir', 'Concluir a OS sem passar pelo pagamento'],
      ['os.entregar', 'Entregar o veículo ao cliente'],
      ['os.entregar_pendente', 'Liberar o carro com o pagamento pendente'],
      ['os.reabrir', 'Reabrir OS fechada (48h) e descancelar'],
    ],
  },
  {
    key: 'cobranca',
    label: 'Cobrança',
    permissions: [
      ['cobranca.receber', 'Registrar pagamento do cliente'],
      ['cobranca.desconto', 'Dar desconto'],
    ],
  },
  {
    key: 'financeiro',
    label: 'Financeiro',
    permissions: [
      ['financeiro.ver', 'Abrir a tela Financeiro: a receber, contas a pagar e exportação'],
      ['financeiro.lancar', 'Contas a pagar e sinistro: lançar, dar baixa, excluir'],
      ['painel.faturamento', 'Ver o faturamento no dashboard, nos relatórios e no desempenho da equipe'],
    ],
  },
  {
    key: 'estoque',
    label: 'Estoque e compras',
    permissions: [
      ['estoque.custo', 'Ver o custo de compra e a margem'],
      ['estoque.movimentar', 'Dar entrada, saída e ajuste manual'],
      ['estoque.cadastrar', 'Criar e editar item e preço de venda'],
      ['compras.pedir', 'Montar pedido de compra'],
      ['compras.aprovar', 'Aprovar e confirmar pedido (gera conta a pagar)'],
      ['fornecedores.gerir', 'Cadastrar, editar e excluir fornecedor'],
    ],
  },
  {
    key: 'clientes',
    label: 'Clientes e agenda',
    permissions: [
      ['clientes.editar', 'Corrigir nome e telefone do cliente'],
      ['clientes.exportar', 'Exportar a lista de OS e de clientes'],
      ['agenda.gerir', 'Confirmar, remarcar e recusar agendamento'],
    ],
  },
  {
    key: 'equipe',
    label: 'Equipe',
    permissions: [
      ['equipe.ver_desempenho', 'Ver o desempenho de cada mecânico'],
      ['equipe.convidar', 'Convidar e remover pessoas'],
      ['equipe.permissoes', 'Mudar o papel e o que cada um pode fazer'],
      ['rh.gerir', 'Pagamento de mecânico, escala e ausência'],
    ],
  },
  {
    key: 'oficina',
    label: 'Oficina',
    permissions: [
      ['painel.ver', 'Abrir o painel de gestão'],
      ['oficina.configurar', 'Perfil, vagas, módulos, etapas, prestadores'],
      ['oficina.precos', 'Tabela de mão de obra e pacotes'],
    ],
  },
] as const;

type Group = (typeof PERMISSION_GROUPS)[number];
export type Permission = Group['permissions'][number][0];
export type PermissionGroupKey = Group['key'];

export const PERMISSIONS: readonly Permission[] = PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map(([key]) => key),
);

export const PERMISSION_LABELS = Object.fromEntries(
  PERMISSION_GROUPS.flatMap((g) => g.permissions.map(([key, label]) => [key, label])),
) as Record<Permission, string>;

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/**
 * Papéis gravados em `workshop_members.role`. O dono não é papel: é o gestor
 * marcado com `is_owner` — assim nenhum código que já compara o papel com
 * `'manager'` precisou mudar.
 *
 * Recepção e Financeiro (Fase 2) são papéis da OFICINA: a conta dessas pessoas
 * continua no perfil de mecânico do app (`users.role`) até a Fase 5 — ver
 * `accountRoleFor`.
 */
export const MEMBER_ROLES = ['manager', 'mechanic', 'reception', 'finance'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

/**
 * O perfil da CONTA (`users.role`, que escolhe as telas do app) para um papel
 * da oficina. Só o gestor tem perfil próprio; o resto entra pelo de mecânico —
 * é o que os apps instalados sabem abrir.
 */
export function accountRoleFor(role: MemberRole): 'manager' | 'mechanic' {
  return role === 'manager' ? 'manager' : 'mechanic';
}

export function isMemberRole(value: unknown): value is MemberRole {
  return typeof value === 'string' && (MEMBER_ROLES as readonly string[]).includes(value);
}

/** O que só o gestor fazia antes deste catálogo — e por isso o Mecânico não tem. */
const MANAGER_ONLY: readonly Permission[] = [
  'os.reabrir',
  'financeiro.ver',
  'financeiro.lancar',
  'equipe.convidar',
  'equipe.permissoes',
  'rh.gerir',
  'oficina.configurar',
  'oficina.precos',
];

/**
 * O padrão de cada papel. O Mecânico é exatamente o que o mecânico consegue
 * fazer hoje (D10): tudo que a API deixava qualquer membro fazer, e nada do que
 * exigia gestor. O "Ver financeiro" de antes vira ajuste da participação.
 *
 * Recepção e Financeiro são novos, então o padrão deles é decisão nova, sem
 * afetar ninguém (a matriz do plano, com as decisões da prévia de 16/10).
 */
export const ROLE_DEFAULTS: Record<MemberRole, ReadonlySet<Permission>> = {
  manager: new Set(PERMISSIONS),
  mechanic: new Set(PERMISSIONS.filter((p) => !MANAGER_ONLY.includes(p))),
  // Atende no balcão: abre OS, orça, agenda, cobra. Não executa e não mexe em
  // estoque. Desconto e carro saindo sem pagar ficam com quem o gestor liga
  // (decisões 1 e 2 da prévia).
  reception: new Set<Permission>([
    'os.abrir',
    'os.itens',
    'os.preco_manual',
    'os.orcamento',
    'os.atribuir',
    'os.cancelar',
    'os.concluir',
    'os.entregar',
    'cobranca.receber',
    'clientes.editar',
    'clientes.exportar',
    'agenda.gerir',
    'painel.ver',
  ]),
  // Cuida das contas: cobra, paga fornecedor, aprova compra, paga os
  // mecânicos (decisão 3). Não mexe na OS.
  finance: new Set<Permission>([
    'os.concluir',
    'os.entregar_pendente',
    'cobranca.receber',
    'cobranca.desconto',
    'financeiro.ver',
    'financeiro.lancar',
    'painel.faturamento',
    'estoque.custo',
    'compras.pedir',
    'compras.aprovar',
    'fornecedores.gerir',
    'clientes.exportar',
    'equipe.ver_desempenho',
    'rh.gerir',
    'painel.ver',
  ]),
};

/** O ajuste da pessoa: só o que difere do padrão do papel. */
export type PermissionOverrides = Partial<Record<Permission, boolean>>;

/**
 * Lê o jsonb gravado sem confiar nele: chave desconhecida e valor que não é
 * booleano são descartados (a lição do F113 — o schema era a única barreira
 * contra `is_superadmin` entrar por um PATCH de permissões).
 */
export function parsePermissionOverrides(raw: unknown): PermissionOverrides {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: PermissionOverrides = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isPermission(key) && typeof value === 'boolean') out[key] = value;
  }
  return out;
}

export interface MembershipForPermissions {
  /** `workshop_members.role`. Texto livre no banco: o legado pode ter outros valores. */
  role: string | null;
  isOwner?: boolean;
  overrides?: unknown;
}

/**
 * As permissões efetivas de uma participação.
 *
 * - Dono: tudo, sempre. Nada do dono é ajustável.
 * - Gestor e mecânico: o padrão do papel com os ajustes por cima.
 * - Papel desconhecido, ou sem participação (`null`): o padrão do Mecânico.
 *   Até este catálogo, quem não era gestor passava em toda rota que só pedia
 *   login — é isso que se preserva (D10). O que já era barrado continua
 *   barrado nas próprias rotas.
 */
export function effectivePermissions(membership: MembershipForPermissions | null): ReadonlySet<Permission> {
  if (membership?.isOwner) return ROLE_DEFAULTS.manager;
  const stored = membership?.role;
  const role: MemberRole = isMemberRole(stored) ? stored : 'mechanic';
  const overrides = parsePermissionOverrides(membership?.overrides);
  if (Object.keys(overrides).length === 0) return ROLE_DEFAULTS[role];

  const result = new Set(ROLE_DEFAULTS[role]);
  for (const [key, allowed] of Object.entries(overrides) as [Permission, boolean][]) {
    if (allowed) result.add(key);
    else result.delete(key);
  }
  return result;
}

/**
 * O ajuste que leva o papel ao conjunto desejado — só as diferenças. Gravar um
 * valor igual ao padrão não deixa chave: assim, quando o produto muda o padrão
 * de um papel, quem não foi ajustado naquele ponto recebe o padrão novo.
 */
export function overridesFromEdits(role: MemberRole, desired: Iterable<Permission>): PermissionOverrides {
  const want = new Set(desired);
  const base = ROLE_DEFAULTS[role];
  const out: PermissionOverrides = {};
  for (const p of PERMISSIONS) {
    const has = want.has(p);
    if (has !== base.has(p)) out[p] = has;
  }
  return out;
}

/**
 * O ajuste a gravar quando se pede `overrides` para um papel: só o que difere do
 * padrão dele, sem chave desconhecida (F113). É o que mantém "voltar ao padrão"
 * igual a "nenhum ajuste" — gravar um valor igual ao padrão não deixa rastro.
 */
export function normalizeOverrides(role: MemberRole, raw: unknown): PermissionOverrides {
  const parsed = parsePermissionOverrides(raw);
  const base = ROLE_DEFAULTS[role];
  const out: PermissionOverrides = {};
  for (const [p, value] of Object.entries(parsed) as Array<[Permission, boolean]>) {
    if (base.has(p) !== value) out[p] = value;
  }
  return out;
}

/** O nome do papel, como o gestor lê. */
export const ROLE_LABELS: Record<MemberRole, string> = {
  manager: 'Gestor',
  mechanic: 'Mecânico',
  reception: 'Recepção',
  finance: 'Financeiro',
};

/** O que o papel é, numa linha — o texto da ficha e do convite. */
export const ROLE_DESCRIPTIONS: Record<MemberRole, string> = {
  manager: 'Sócio ou gerente. Faz tudo, menos o que é só do dono.',
  mechanic: 'Põe a mão no carro. Abre OS, executa, orça. Não mexe em dinheiro nem em configuração.',
  reception: 'Atende no balcão. Abre OS, orça, agenda, cobra. Não executa e não mexe em estoque.',
  finance: 'Cuida das contas. Cobra, paga fornecedor, aprova compra. Não mexe na OS.',
};

/**
 * O que é preciso ter para CONVIDAR alguém com este papel: tudo o que o papel
 * recebe (regra 4 do D7 — só concede o que tem). Convidar gestor é conceder
 * tudo o que o gestor tem, como na promoção.
 */
export function canInviteRole(actorPerms: ReadonlySet<Permission>, role: MemberRole): boolean {
  return [...ROLE_DEFAULTS[role]].every((p) => actorPerms.has(p));
}

export interface MemberForChange {
  userId: string;
  role: string | null;
  isOwner?: boolean;
  overrides?: unknown;
}

export type ChangeMemberResult =
  | { ok: true }
  | { ok: false; reason: 'no_permission' | 'self' | 'owner' | 'outranks' | 'exceeds_actor'; permissions?: Permission[] };

/**
 * Uma mudança de papel ou de ajustes numa pessoa é permitida? Regras contra
 * escalada (D7, revisadas pela D10):
 *
 * 1. Quem muda precisa de `equipe.permissoes`.
 * 2. Ninguém muda a si mesmo.
 * 3. Ninguém mexe no dono.
 * 4. Só concede o que tem: o que a mudança ACRESCENTA ao alvo precisa estar no
 *    conjunto de quem muda (o dono tem tudo, então passa sempre). O que o alvo
 *    já tinha não conta — senão um gestor que perdeu uma permissão não salvaria
 *    mais ninguém que a tivesse por padrão.
 *
 * 5. Para mudar ou remover um GESTOR, é preciso ter tudo o que o gestor tem
 *    (`canActOnMember`). O gestor continua mexendo em outro gestor, como hoje;
 *    o mecânico a quem o dono liberou "mudar a equipe" mexe nos mecânicos, não
 *    nos gestores — senão ele rebaixaria quem o supervisiona.
 *
 * Enquanto as rotas "só gestor" decidirem pelo PAPEL (`requireWorkshopManager`
 * olha `role === 'manager'`, não as permissões), virar gestor concede tudo o
 * que o gestor tem, com ou sem ajuste — e é isso que conta na regra 4. Senão um
 * gestor sem `rh.gerir` promoveria alguém "a gestor sem RH" e o promovido
 * entraria nas rotas de RH pelo papel.
 */
/**
 * A regra 5 de `canChangeMember`, sozinha — vale também para REMOVER alguém:
 * para mexer num gestor, é preciso ter tudo o que o gestor tem.
 */
export function canActOnMember(actorPerms: ReadonlySet<Permission>, target: MemberForChange): boolean {
  if (target.role !== 'manager') return true;
  return [...ROLE_DEFAULTS.manager].every((p) => actorPerms.has(p));
}

export function canChangeMember(
  actor: MemberForChange,
  target: MemberForChange,
  next: { role: MemberRole; overrides: PermissionOverrides },
): ChangeMemberResult {
  const actorPerms = effectivePermissions(actor);
  if (!actorPerms.has('equipe.permissoes')) return { ok: false, reason: 'no_permission' };
  if (actor.userId === target.userId) return { ok: false, reason: 'self' };
  if (target.isOwner) return { ok: false, reason: 'owner' };
  if (!canActOnMember(actorPerms, target)) return { ok: false, reason: 'outranks' };

  const before = target.role === 'manager' ? ROLE_DEFAULTS.manager : effectivePermissions(target);
  const result = next.role === 'manager'
    ? ROLE_DEFAULTS.manager
    : effectivePermissions({ role: next.role, overrides: next.overrides });
  const exceeding = grantsBeyond(actorPerms, before, result);
  if (exceeding.length > 0) return { ok: false, reason: 'exceeds_actor', permissions: exceeding };
  return { ok: true };
}

/** O rótulo da pessoa na equipe: "Dono", ou o papel. Papel legado vale como Mecânico. */
export function memberRoleLabel(role: string | null | undefined, isOwner = false): string {
  if (isOwner) return 'Dono';
  return ROLE_LABELS[isMemberRole(role) ? role : 'mechanic'];
}

/**
 * O que uma mudança ACRESCENTA que quem muda não tem — a regra 4 do D7, para a
 * tela desabilitar o que a API recusaria. `before`/`after` são o efetivo do
 * alvo antes e depois.
 */
export function grantsBeyond(
  actorPerms: ReadonlySet<Permission>,
  before: ReadonlySet<Permission>,
  after: ReadonlySet<Permission>,
): Permission[] {
  return [...after].filter((p) => !before.has(p) && !actorPerms.has(p));
}

export interface TeamEventForText {
  action: string;
  actor: { name: string } | null;
  target: { name: string } | null;
  details: Record<string, unknown>;
}

function roleText(value: unknown): string {
  return typeof value === 'string' ? memberRoleLabel(value) : 'Mecânico';
}

function permissionList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter(isPermission).map((p) => PERMISSION_LABELS[p]) : [];
}

/**
 * A frase de um evento do histórico da equipe (`workshop_team_events`), como o
 * gestor lê — a mesma no painel e no app. Responde "quem tirou o desconto do
 * Carlos?".
 */
export function describeTeamEvent(event: TeamEventForText): { title: string; detail: string | null } {
  const actor = event.actor?.name ?? 'Alguém';
  const target = event.target?.name ?? 'alguém';
  const d = event.details;
  switch (event.action) {
    case 'member_invited':
      // `renewed`: gerar de novo devolve o mesmo código com prazo novo (antes
      // trocava o código, e o anterior parava de valer — `replaced`).
      if (d.renewed === true) {
        return { title: `${actor} renovou o convite de ${roleText(d.role)}`, detail: 'Mesmo código, com o prazo renovado' };
      }
      return { title: `${actor} gerou um convite de ${roleText(d.role)}`, detail: d.replaced ? 'O convite anterior desse papel parou de valer' : null };
    case 'invite_revoked':
      return { title: `${actor} revogou um convite`, detail: null };
    case 'member_joined':
      return { title: `${target} entrou como ${roleText(d.role)}`, detail: 'Pelo convite' };
    case 'member_removed':
      return { title: `${actor} removeu ${target} da oficina`, detail: null };
    case 'role_changed':
      return {
        title: `${actor} mudou ${target} de ${roleText(d.from)} para ${roleText(d.to)}`,
        detail: d.overridesCleared ? 'Os ajustes voltaram ao padrão do papel' : null,
      };
    case 'permissions_changed': {
      const granted = permissionList(d.granted);
      const revoked = permissionList(d.revoked);
      if (granted.length === 1 && revoked.length === 0) return { title: `${actor} liberou "${granted[0]}" para ${target}`, detail: null };
      if (revoked.length === 1 && granted.length === 0) return { title: `${actor} tirou "${revoked[0]}" de ${target}`, detail: null };
      const parts = [granted.length ? `Liberou: ${granted.join(', ')}` : '', revoked.length ? `Tirou: ${revoked.join(', ')}` : ''].filter(Boolean);
      return { title: `${actor} mudou o que ${target} pode fazer`, detail: parts.join(' · ') || null };
    }
    case 'owner_transferred':
      return { title: `${actor} passou a posse da oficina para ${target}`, detail: null };
    case 'works_on_cars_changed': {
      const on = d.value === true;
      if (d.self === true) return { title: on ? `${actor} passou a trabalhar nos carros` : `${actor} deixou de trabalhar nos carros`, detail: null };
      return { title: on ? `${actor} marcou que ${target} trabalha nos carros` : `${actor} marcou que ${target} não trabalha nos carros`, detail: null };
    }
    default:
      return { title: `${actor} mudou a equipe`, detail: null };
  }
}

/**
 * O que uma mudança de status exige (Fase 3 — "Fechar a OS: cinco momentos,
 * três permissões"). A mesma regra na rota de status, no sync e nas telas.
 *
 * - cancelar → `os.cancelar`;
 * - concluir pela rota de status ou pelo sync → `os.concluir`. A conclusão
 *   que vem de RECEBER o pagamento é da rota de pagamento (`cobranca.receber`),
 *   não passa por aqui;
 * - entregar → `os.entregar`; vindo de "aguardando pagamento" (o fiado),
 *   `os.entregar_pendente` — e só ela: liberar o carro sem pagamento já é
 *   entregar, e é assim que o Financeiro, que não entrega carro pago, libera
 *   o fiado;
 * - o resto (iniciar, aguardar peça, terceirizar, "finalizar serviço") é o
 *   andamento do trabalho → `os.executar`.
 *
 * Reabrir e descancelar têm rota própria (`os.reabrir`).
 */
export function statusChangePermissions(from: string | null | undefined, to: string): Permission[] {
  if (to === 'cancelled') return ['os.cancelar'];
  if (to === 'completed') return ['os.concluir'];
  if (to === 'delivered') return from === 'waiting_payment' ? ['os.entregar_pendente'] : ['os.entregar'];
  return ['os.executar'];
}

/** As permissões de `required` que `perms` não tem. */
export function missingPermissions(perms: ReadonlySet<Permission>, required: readonly Permission[]): Permission[] {
  return required.filter((p) => !perms.has(p));
}

/**
 * Uma mudança que o sync recusou por falta de permissão (D8). O servidor aplica
 * o resto da OS e devolve isto; o aparelho avisa a pessoa, e o pull seguinte
 * desfaz a mudança local.
 */
export interface PermissionDeniedChange {
  table: 'service_orders' | 'customers';
  /** O id LOCAL do registro (WatermelonDB). */
  id: string;
  /** `create`, `status`, `items`, `stage`, `paint_readings`, `consumables`, `paint`, `slot`, `deadline`, `internal_notes`, `customer`. */
  field: string;
  permission: Permission;
}

/**
 * Versão do contrato de permissões que o app entende (D8). O app manda este
 * número no registro do aparelho (`PUT /users/me/push-token`), em todo cold
 * start, e a ficha da pessoa avisa o gestor quando algum aparelho dela ainda
 * não entende — o app antigo mostra o que o gestor tirou (o servidor barra,
 * sem explicar) e R$ 0,00 no lugar do valor escondido.
 *
 * Por que não a versão do app: esta fase sai por OTA sobre a 1.10.0, e o OTA
 * não muda o `version` (o `runtimeVersion` é a própria versão). Este número
 * mora no bundle JS, então viaja junto com o OTA.
 *
 * 1 = Fase 3c: `useCan`, avisos de `permissionDenied` e da fila de mutações.
 * Suba quando o app passar a respeitar algo que a versão anterior não respeita.
 */
export const PERMISSIONS_CLIENT_VERSION = 1;

/**
 * O que o app anterior às permissões ainda oferece a esta pessoa e ela não pode
 * fazer — o que o aviso de app antigo da ficha tem para contar. Esse app só
 * conhece o perfil da conta: o gestor vê o de gestor, o resto (Mecânico,
 * Recepção, Financeiro, papel antigo) vê o que o mecânico sempre viu
 * (`accountRoleFor`). Vazio = o app antigo não engana sobre nada, ainda.
 */
export function oldAppStillOffers(membership: MembershipForPermissions): Permission[] {
  if (membership.isOwner) return [];
  const role: MemberRole = isMemberRole(membership.role) ? membership.role : 'mechanic';
  const has = effectivePermissions(membership);
  return [...ROLE_DEFAULTS[accountRoleFor(role)]].filter((p) => !has.has(p));
}

/**
 * A pessoa trabalha nos carros nesta oficina (perfil único, 5a)? O Mecânico
 * sempre; os outros papéis quando o gestor liga a marca na ficha
 * (`workshop_members.works_on_cars`). Mesma regra do `worksOnCarsSql` da API.
 */
export function memberWorksOnCars(membership: { role: string | null; worksOnCars?: boolean | null }): boolean {
  return membership.role === 'mechanic' || membership.worksOnCars === true;
}

/** As duas árvores de telas do app da oficina. */
export type WorkshopApp = 'manager' | 'mechanic';

/**
 * Qual app a pessoa abre NESTA oficina (perfil único, 5a) — decidido pelo papel
 * na oficina ativa, não por um perfil escolhido no aparelho. Gestor, Recepção e
 * Financeiro trabalham na gestão e abrem o do gestor; o Mecânico (e o papel
 * antigo, que vale como Mecânico) abre o do mecânico.
 *
 * Sem o painel liberado (`painel.ver`), o do mecânico: o do gestor seria só a
 * tela trancada. As coisas de mecânico de quem também trabalha nos carros
 * ficam dentro do app do gestor ("Nova OS", "Minhas OS").
 *
 * Recebe as permissões EFETIVAS porque é o que o app tem guardado do `/me`.
 */
export function workshopAppFor(role: string | null | undefined, permissions: ReadonlySet<string>): WorkshopApp {
  const management = role === 'manager' || role === 'reception' || role === 'finance';
  return management && permissions.has('painel.ver') ? 'manager' : 'mechanic';
}
